import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { DEFAULT_JOB_TYPES, paths, type Tenant } from "@clevis/shared-types";
import type { JobWorkflow } from "@clevis/shared-types";
import { db } from "../lib/firebase";

export type JobTypePay = NonNullable<Tenant["jobTypePay"]>;

// Job types and how each pays the tech (Jobs → Job settings).
export function useJobSettings(tenantId: string | null): {
  types: string[];
  pay: JobTypePay;
  pieceSplit: "even" | "each";
  /** Tech-logged parts: paid as pieces right away, or only after a manager's OK. */
  pieceExtras: "auto" | "approve";
  /** Per type: job-page sections field techs don't get. */
  flows: Record<string, string[]>;
  /** Per type: the tech's steps (on my way, partial, dry run…); unset types use the default for their name. */
  workflows: Record<string, Partial<JobWorkflow>>;
} {
  const [state, setState] = useState<{ types: string[]; pay: JobTypePay; pieceSplit: "even" | "each"; pieceExtras: "auto" | "approve"; flows: Record<string, string[]>; workflows: Record<string, Partial<JobWorkflow>> }>({
    types: DEFAULT_JOB_TYPES,
    pay: {},
    pieceSplit: "even",
    pieceExtras: "auto",
    flows: {},
    workflows: {},
  });
  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(doc(db, paths.tenant(tenantId)), (snap) => {
      const t = snap.get("jobTypes") as string[] | undefined;
      setState({
        types: t && t.length > 0 ? t : DEFAULT_JOB_TYPES,
        pay: (snap.get("jobTypePay") as JobTypePay | undefined) ?? {},
        pieceSplit: (snap.get("pieceSplit") as "even" | "each" | undefined) ?? "even",
        pieceExtras: (snap.get("pieceExtras") as "auto" | "approve" | undefined) ?? "auto",
        flows: (snap.get("jobTypeFlows") as Record<string, string[]> | undefined) ?? {},
        workflows: (snap.get("jobTypeWorkflows") as Record<string, Partial<JobWorkflow>> | undefined) ?? {},
      });
    });
  }, [tenantId]);
  return state;
}
